/* screens/support/SupportChat.jsx — общие части чата поддержки, визуально как в
   Ассистенте: те же классы .msg/.msg__bubble (пузыри) и .composer (поле ввода),
   чип вложения AttachmentChip. Переиспользуется страницей юзера и дровером оператора. */
import React from 'react';
import { Button } from '../../components/ui/Button.jsx';
import { Textarea } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { AttachmentChip } from '../../components/ui/AttachmentChip.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { takeFiles, usableFiles } from './pickFiles.js';

const { useState, useRef } = React;

function timeShort(t) {
  const d = t instanceof Date ? t : new Date(t);
  if (isNaN(d)) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function Lines({ text }) {
  return String(text || '').split('\n').map((ln, i) => <p key={i} className="msg__line">{ln}</p>);
}
const toChip = (a) => ({ name: a.name, size: a.size, mime: a.content_type, url: a.url });

function SupportMessage({ m, mineSide }) {
  if (m.is_system) {
    return (
      <div className="feed-item" style={{ textAlign: 'center' }}>
        <span className="t-caption text-mute"
          style={{ background: 'var(--surface-2,#f1f5f9)', padding: '4px 12px', borderRadius: 999, display: 'inline-block' }}>
          {m.body}
        </span>
      </div>
    );
  }
  const atts = m.attachments || [];
  const mine = m.sender_type === mineSide;

  if (mine) {
    return (
      <div className="feed-item">
        <div className="msg msg--user">
          <div className="msg__col">
            {m.body && <div className="msg__bubble msg__bubble--user"><Lines text={m.body} /></div>}
            {atts.length > 0 && (
              <div className="msg__atts">{atts.map((a) => <AttachmentChip key={a.id} file={toChip(a)} inFeed />)}</div>
            )}
            <div className="msg__meta msg__meta--user num">{timeShort(m.created_at)}</div>
          </div>
        </div>
      </div>
    );
  }

  // «их» сторона: оператор → пользователю ВСЕГДА бренд «Ресториум» + логотип
  // (личность оператора не раскрываем); пользователь → его имя + ФОТО, если есть.
  const isSupport = m.sender_type === 'operator';
  const author = isSupport
    ? (mineSide === 'user' ? 'Ресториум' : (m.sender_label || 'Оператор'))
    : (m.sender_label || 'Пользователь');
  const photo = !isSupport ? (m.sender_avatar || '') : '';
  return (
    <div className="feed-item">
      <div className="msg msg--assistant">
        <div className="msg__avatar" aria-hidden="true">
          {isSupport
            ? <img src="/logo-mark.svg" alt="" width={22} height={22} />
            : (photo
                ? <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                : <span style={{ fontSize: 12, fontWeight: 600 }}>{(author[0] || '?').toUpperCase()}</span>)}
        </div>
        <div className="msg__col">
          <div className="msg__meta"><span className="msg__author">{author}</span> <span className="num">{timeShort(m.created_at)}</span></div>
          {m.body && <div className="msg__bubble"><Lines text={m.body} /></div>}
          {atts.length > 0 && (
            <div className="msg__atts">{atts.map((a) => <AttachmentChip key={a.id} file={toChip(a)} inFeed />)}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/** mineSide: какой sender_type — «мой» ('user' на стр. юзера, 'operator' у оператора). */
function MessageList({ messages, mineSide }) {
  return (
    <div className="msg-list__inner">
      {(messages || []).map((m) => <SupportMessage key={m.id} m={m} mineSide={mineSide} />)}
    </div>
  );
}

/** Поле ввода как у Ассистента: скрепка + авто-рост textarea + отправка (Enter).
 *  onSend(body, files) — ОБЯЗАН пробрасывать ошибку, иначе набранное будет
 *  стёрто при отказе сервера (см. submit).
 *  blocked/blockedHint — отправка запрещена, пока форма не дозаполнена (телефон). */
function Composer({ onSend, placeholder, blocked = false, blockedHint = '' }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pick = (e) => {
    const { usable, lost } = usableFiles(takeFiles(e.target));
    if (usable.length) setFiles((p) => [...p, ...usable]);
    // Тишина после выбора файла — ровно то, на что жаловалась поддержка: окно
    // выбора открылось, файл выбран, дальше НИЧЕГО. Пустой `change` браузеры на
    // отмене не шлют, так что сюда попадает только нечитаемый выбор — говорим
    // об этом вслух, а не оставляем оператора гадать.
    if (lost) {
      const t = toast && toast.show;
      if (t) {
        t(usable.length
          ? 'Часть файлов не прочиталась — они не прикреплены.'
          : 'Файл не прочитался. Если это фото из iCloud — откройте его в «Фото» и повторите.',
        'danger');
      }
    }
  };
  const removeFile = (i) => setFiles((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    const body = text.trim();
    if ((!body && !files.length) || busy || blocked) return;
    setBusy(true);
    // Поле чистим ТОЛЬКО после успеха. Раньше чистили безусловно, и отказ сервера
    // (например, «проверьте номер телефона») стирал набранный вопрос вместе с
    // прикреплёнными файлами: человек видел тост и пустую форму, а описывать
    // проблему заново — ровно тот момент, когда обращение бросают.
    try { await onSend(body, files); setText(''); setFiles([]); }
    catch { /* текст и вложения остаются на месте — можно поправить и повторить */ }
    finally { setBusy(false); }
  };
  const onKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } };
  const canSend = !busy && !blocked && (text.trim() || files.length);

  return (
    <div className="composer">
      <div className="composer__inner">
        {blocked && blockedHint && (
          <div className="t-caption text-mute" style={{ padding: '2px 4px 6px' }} role="status">
            {blockedHint}
          </div>
        )}
        {files.length > 0 && (
          <div className="composer__atts">
            {files.map((f, i) => (
              <AttachmentChip key={i} file={{ name: f.name, size: f.size, mime: f.type }}
                onRemove={() => removeFile(i)} />
            ))}
          </div>
        )}
        <div className="composer__row">
          <button className="composer__icon-btn" onClick={() => fileRef.current && fileRef.current.click()}
            aria-label="Прикрепить файл" disabled={busy}>
            <Icon name="paperclip" size={18} />
          </button>
          <input ref={fileRef} type="file" multiple hidden onChange={pick} />
          <Textarea className="composer__ta" minRows={1} maxRows={9}
            placeholder={placeholder || 'Сообщение…'} value={text}
            onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown} aria-label="Сообщение" />
          <Button variant="primary" iconOnly="send" className="composer__send"
            onClick={submit} disabled={!canSend} aria-label="Отправить" />
        </div>
      </div>
    </div>
  );
}

export { MessageList, Composer };
